/**
 * Claim review endpoints (PRD-0239 D-8) — the queue, review detail, metrics
 * and the five decision verbs (PRD-0241 D-8 adds retire). All URLs include
 * trailing slash to avoid FastAPI 307 redirects; If-Match is REQUIRED on
 * every decision call (R-4), so every one of the five passes it through
 * `ifMatchHeaders` rather than the optional variant
 * `services/api/semanticMemory.ts` uses for the landed curatorial `/status/`
 * route.
 */

import type {
  ClaimCorrectionRequest,
  ClaimCorrectionResponse,
  ClaimDisputeResolutionRequest,
  ClaimDisputeResolutionResponse,
  ClaimRejectRequest,
  ClaimResponse,
  ClaimRetireRequest,
  ClaimReviewDetailResponse,
  ClaimReviewMetricsResponse,
  ClaimReviewQueueFilters,
  ClaimReviewQueueResponse,
} from '@/types/smem'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'

export const claimReview = {
  list: async (filters?: ClaimReviewQueueFilters): Promise<ClaimReviewQueueResponse> => {
    const response = await apiClient.get<ClaimReviewQueueResponse>(
      '/api/v1/semantic-memory/claims/review-queue/',
      { params: filters, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  detail: async (id: string): Promise<ClaimReviewDetailResponse> => {
    const response = await apiClient.get<ClaimReviewDetailResponse>(
      `/api/v1/semantic-memory/claims/${id}/review-detail/`
    )
    return response.data
  },

  metrics: async (windowDays: number): Promise<ClaimReviewMetricsResponse> => {
    const response = await apiClient.get<ClaimReviewMetricsResponse>(
      '/api/v1/semantic-memory/claims/review-metrics/',
      { params: { window_days: windowDays } }
    )
    return response.data
  },

  approve: async (id: string, ifMatch: number): Promise<ClaimResponse> => {
    const response = await apiClient.post<ClaimResponse>(
      `/api/v1/semantic-memory/claims/${id}/approve/`,
      {},
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  reject: async (
    id: string,
    ifMatch: number,
    body: ClaimRejectRequest
  ): Promise<ClaimResponse> => {
    const response = await apiClient.post<ClaimResponse>(
      `/api/v1/semantic-memory/claims/${id}/reject/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  correct: async (
    id: string,
    ifMatch: number,
    body: ClaimCorrectionRequest
  ): Promise<ClaimCorrectionResponse> => {
    const response = await apiClient.post<ClaimCorrectionResponse>(
      `/api/v1/semantic-memory/claims/${id}/correct/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  resolveDispute: async (
    id: string,
    ifMatch: number,
    body: ClaimDisputeResolutionRequest
  ): Promise<ClaimDisputeResolutionResponse> => {
    const response = await apiClient.post<ClaimDisputeResolutionResponse>(
      `/api/v1/semantic-memory/claims/${id}/resolve-dispute/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  retire: async (
    id: string,
    ifMatch: number,
    body: ClaimRetireRequest
  ): Promise<ClaimResponse> => {
    const response = await apiClient.post<ClaimResponse>(
      `/api/v1/semantic-memory/claims/${id}/retire/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },
}
